import axios from 'axios';
import { toast } from 'react-toastify';

const url = window.location.hostname.includes('localhost') ? "http://localhost:8080" : "https://mydash.yorkdistribuidora.com.br:35001";

export const BASE_URL = url;

const api = axios.create({
  baseURL: BASE_URL
});

const apiFile = axios.create({
  baseURL: BASE_URL,
  responseType: "blob"
});

class ApiService {

  constructor(apiurl) {
    this.apiurl = apiurl;
  }

  post(url, obj, config) {
    const requestUrl = `${this.apiurl}${url}`
    return api.post(requestUrl, obj, config);
  }

  put(url, obj) {
    const requestUrl = `${this.apiurl}${url}`
    return api.put(requestUrl, obj);
  }

  delete(url) {
    const requestUrl = `${this.apiurl}${url}`
    return api.delete(requestUrl)
  }

  get(url) {
    const requestUrl = `${this.apiurl}${url}`
    return api.get(requestUrl)
  }

  patch(url, obj) {
    const requestUrl = `${this.apiurl}${url}`
    return api.patch(requestUrl, obj)
  }

  fileDownload(url) {
    const requestUrl = `${this.apiurl}${url}`
    return apiFile.get(requestUrl)
  }


  async getFile(url) {
    try {
      const response = await apiFile.get(`${this.apiurl}${url}`);
      return response;
    } catch (error) {
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.readAsText(error.response.data);

        return new Promise((resolve, reject) => {
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              if (errorData && errorData.message) {
                reject(errorData);
              } else {
                reject(error);
              }
            } catch (parseError) {
              reject(error);
            }
          };

          reader.onerror = () => {
            reject(error);
          };
        });
      } else {
        return Promise.reject(error);
      }
    }
  }
}

api.interceptors.request.use(config => {
  const token = localStorage.getItem('@BI:Token') || '';

  if (!token) return config;

  if (config?.headers) {
    config.headers = { Authorization: `Bearer ${token}` }
  }
  return config;
});

apiFile.interceptors.request.use(config => {
  const token = localStorage.getItem('@BI:Token') || '';

  if (!token) return config;

  if (config?.headers) {
    config.headers = { Authorization: `Bearer ${token}` }
  }
  return config;
});

const TOKEN_KEY = '@BI:Token';
const REFRESH_TOKEN_KEY = '@BI:RefreshToken';
// Endpoints que nunca devem tentar renovar sozinhos, senão um 401 neles viraria um loop
// (o próprio refresh falhando tentaria se renovar de novo) ou trocaria login/senha errados
// por uma mensagem de "sessão expirada" que não é o que aconteceu.
const URLS_SEM_RENOVACAO = ["/api/usuarios/autenticar", "/api/usuarios/refresh-token", "/api/usuarios/logout"];

// Várias requisições podem tomar 401 ao mesmo tempo (a mesma tela dispara N chamadas em paralelo);
// sem isso, cada uma dispararia sua própria renovação — a primeira resposta do backend já revogaria
// o refresh token (rotativo, ver RefreshTokenService), fazendo as demais falharem. Compartilhando a
// mesma promise, só a primeira chamada realmente bate no backend; as outras esperam o resultado dela.
let renovacaoEmAndamento = null;

function renovarToken() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return Promise.reject(new Error("Sem refresh token"));
  }

  if (!renovacaoEmAndamento) {
    renovacaoEmAndamento = axios.post(`${BASE_URL}/api/usuarios/refresh-token`, { refreshToken })
      .then(res => {
        localStorage.setItem(TOKEN_KEY, res.data.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, res.data.refreshToken);
        return res.data.token;
      })
      .finally(() => { renovacaoEmAndamento = null; });
  }
  return renovacaoEmAndamento;
}

function encerrarSessaoExpirada() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('@BI:User');
  toast.error("Sua sessão expirou. Faça login novamente.");
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

function podeRenovar(config) {
  return !!config && !config._tentouRenovar && !URLS_SEM_RENOVACAO.some(u => config.url?.includes(u));
}

api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;
    const originalRequest = error.config;

    if (status === 401 && podeRenovar(originalRequest)) {
      originalRequest._tentouRenovar = true;
      return renovarToken()
        .then(novoToken => {
          originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${novoToken}` };
          return api(originalRequest);
        })
        .catch(() => {
          encerrarSessaoExpirada();
          return Promise.reject(error);
        });
    }

    const message = error.response?.data?.message;
    const code = error.code;

    if (!error.response) {
      if (code === "ERR_NETWORK") {
        toast.error("ERR_NETWORK! Aguarde alguns instantes e tente novamente.");
      } else if (code === "ECONNABORTED") {
        toast.error("Servidor demorou para responder. Tente novamente.");
      } else {
        toast.error("Falha de conexão com o servidor.");
      }

      return Promise.reject(error);
    }

    switch (status) {
      case 400:
        toast.error(message || "Erro 400");
        break;
      case 401:
        toast.error(message || "Erro 401");
        break;
      case 403:
        toast.error(message || "Erro 403");
        break;
      case 404:
        toast.error(message || "Erro 404");
        break;
      case 500:
        toast.error(
          message || "Ocorreu um erro de sistema! Entre em contato com o administrador."
        );
        break;
      case 502:
      case 503:
      case 504:
        toast.error(
          "Sistema em atualização. Aguarde alguns instantes e tente novamente."
        );
        break;
      default:
        toast.error("Ocorreu um erro inesperado. Aguarde alguns instantes e tente novamente");
    }

    return Promise.reject(error);
  }
);

export default ApiService;